import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { LoadingBlock } from "@/components/ds";
import { Crown, LayoutDashboard, Building2, Users, PieChart, Wallet, Award, LogOut, Home } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/executive", label: "Command Center", icon: LayoutDashboard, end: true },
  { to: "/executive/sales-team", label: "Sales Team", icon: Users },
  { to: "/executive/bonuses", label: "Bonuses", icon: Award },
  { to: "/executive/businesses", label: "Businesses", icon: Building2 },
  { to: "/executive/analytics", label: "Analytics", icon: PieChart },
  { to: "/executive/compensation", label: "Compensation", icon: Wallet },
];

export default function ExecutiveLayout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { data, isLoading, isError } = useQuery({ queryKey: ["internal-me-exec"], queryFn: () => api.internalMe(), retry: false });

  const isMaster = data?.is_senior_admin;
  const isRep = data?.role === "sales_rep";

  if (isLoading) return <div className="min-h-screen bg-slate-950 p-10"><LoadingBlock rows={6} /></div>;
  if (isError || !(isMaster || isRep)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-center" data-testid="executive-denied">
        <div className="max-w-md p-8">
          <Crown className="mx-auto mb-4 h-10 w-10 text-amber-400/70" />
          <h1 className="text-xl font-semibold text-white">Restricted</h1>
          <p className="mt-2 text-sm text-slate-400">The Command Center is available only to the Master Admin and sales reps.</p>
          <button onClick={() => navigate("/")} className="mt-6 rounded-lg bg-white/10 px-4 py-2 text-sm text-white hover:bg-white/20">Return home</button>
        </div>
      </div>
    );
  }

  // Reps only see their own data — hide company-wide screens.
  const nav = isMaster ? NAV : NAV.filter((n) => ["/executive", "/executive/sales-team", "/executive/bonuses"].includes(n.to));

  return (
    <div className="flex min-h-screen bg-slate-950 text-slate-100">
      <aside className="fixed inset-y-0 left-0 w-64 border-r border-white/5 bg-slate-900/60 backdrop-blur">
        <div className="flex items-center gap-2 px-5 py-5">
          <Crown className="h-6 w-6 text-amber-400" />
          <div><p className="text-sm font-semibold text-white">ResFil Executive</p><p className="text-[11px] uppercase tracking-widest text-amber-400/70">Command Center</p></div>
        </div>
        <nav className="mt-2 space-y-1 px-3">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`exec-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? "bg-amber-500/15 text-amber-300" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-4 left-3 right-3 space-y-1">
          {/* Back to where each role works day to day, without signing out: the Master Admin's
              business dashboard, or a rep's own portal. */}
          <button onClick={() => navigate(isMaster ? "/app/dashboard" : "/internal")} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-white/5 hover:text-white" data-testid="exec-dashboard">
            <Home className="h-4 w-4" /> Dashboard
          </button>
          <button onClick={() => { logout(); navigate("/login"); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-white/5 hover:text-white" data-testid="exec-logout">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="ml-64 flex-1 p-8">{children}</main>
    </div>
  );
}
